import React, { useEffect, useRef, useMemo, useCallback } from "react";
import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  Alert,
  Button,
  Divider,
} from "@mui/material";
import { ChatMessage, TypingIndicator } from "./chatTypes";
import MessageBubble from "./MessageBubble";
import ChatInput from "./ChatInput";

interface ChatWindowProps {
  projectId: string;
  currentUserId: string;
  currentUserName: string;
  messages: ChatMessage[];
  typingUsers: TypingIndicator[];
  loading: boolean;
  initialLoading?: boolean;
  error: string | null;
  hasMore?: boolean;
  onSendMessage: (
    content: string,
    messageType: "text" | "image",
    file?: File,
  ) => Promise<void>;
  onLoadMore?: () => void;
  disabled?: boolean;
  uploading?: boolean;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  projectId,
  currentUserId,
  currentUserName,
  messages,
  typingUsers,
  loading,
  initialLoading = false,
  error,
  hasMore = false,
  onSendMessage,
  onLoadMore,
  disabled = false,
  uploading = false,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Group consecutive messages from the same user
  const groupedMessages = useMemo(() => {
    return messages.reduce(
      (groups, message, index) => {
        const previousMessage = messages[index - 1];
        const showAvatar =
          !previousMessage || previousMessage.sender_id !== message.sender_id;

        if (groups.length === 0) {
          return [[{ ...message, showAvatar }]];
        }

        const lastGroup = groups[groups.length - 1];
        const lastMessageInGroup = lastGroup[lastGroup.length - 1];

        if (lastMessageInGroup.sender_id === message.sender_id) {
          lastGroup.push({ ...message, showAvatar: false });
        } else {
          groups.push([{ ...message, showAvatar }]);
        }

        return groups;
      },
      [] as Array<Array<ChatMessage & { showAvatar: boolean }>>,
    );
  }, [messages]);

  const typingIndicatorText = useMemo(() => {
    if (typingUsers.length === 0) return "";
    if (typingUsers.length === 1) {
      return `${typingUsers[0].userName} is typing...`;
    }
    return `${typingUsers.map((u) => u.userName).join(", ")} are typing...`;
  }, [typingUsers]);

  if (initialLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          bgcolor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Project Chat
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {messages.length} message{messages.length !== 1 ? "s" : ""}
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => {}}>
          {error}
        </Alert>
      )}

      {/* Messages Container */}
      <Box
        ref={messagesContainerRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.default",
          p: 2,
          gap: 0.5,
        }}
      >
        {/* Load More Button */}
        {hasMore && (
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Button size="small" onClick={onLoadMore} disabled={loading}>
              {loading ? "Loading..." : "Load More Messages"}
            </Button>
          </Box>
        )}

        {/* Empty State */}
        {messages.length === 0 && !loading && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              minHeight: "300px",
              flexDirection: "column",
              color: "text.secondary",
            }}
          >
            <Typography variant="body2" sx={{ mb: 1 }}>
              No messages yet
            </Typography>
            <Typography variant="caption">
              Start a conversation by sending a message
            </Typography>
          </Box>
        )}

        {/* Messages */}
        {groupedMessages.map((group, groupIndex) => (
          <Box key={`group-${groupIndex}`}>
            {group.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                isOwn={message.sender_id === currentUserId}
                showAvatar={message.showAvatar}
              />
            ))}
          </Box>
        ))}

        {/* Typing Indicator */}
        {typingIndicatorText && (
          <Box
            sx={{ py: 1, px: 2, display: "flex", alignItems: "center", gap: 1 }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontStyle: "italic" }}
            >
              {typingIndicatorText}
            </Typography>
            <Box sx={{ display: "flex", gap: "2px" }}>
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  sx={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    bgcolor: "text.secondary",
                    animation: `pulse 1.4s infinite`,
                    animationDelay: `${i * 0.2}s`,
                    "@keyframes pulse": {
                      "0%, 60%, 100%": {
                        opacity: 0.3,
                      },
                      "30%": {
                        opacity: 1,
                      },
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Messages End Marker */}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <Divider />
      <ChatInput
        onSendMessage={onSendMessage}
        disabled={disabled}
        uploading={uploading}
      />
    </Stack>
  );
};

export default ChatWindow;
